/**
 * Slim shell: fetches a single report + scope_checks + open action items
 * for this report, renders the section components in order. Bridge is
 * conditional on C2 = Partial.
 *
 * Phase 2b additions:
 *   - focusActionItemId resolves to a target_id via getActionItem, which is
 *     passed down so the matching section can auto-expand + scroll + flash
 *     the relevant card (#45).
 *   - Per-section open A# counts are aggregated and passed as `openCount`
 *     so each section's outer header can show "N open" or "Complete" (#43).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Header,
  SpaceBetween,
  Spinner,
  StatusIndicator,
} from "@cloudscape-design/components";
import type { ActionItem, Report, ScopeCheck } from "@/core/api/types";
import { getActionItem, listActionItems } from "@/core/api/action-items";
import { getReport } from "@/sections/scope/api";
import { PlaceholderUpload } from "@/sections/scope/PlaceholderUpload";
import { ScopeSection } from "@/sections/scope/ScopeSection";
import { BridgeSection } from "@/sections/bridge/BridgeSection";
import { ControlsSection } from "@/sections/controls/ControlsSection";
import { CuecsSection } from "@/sections/cuecs/CuecsSection";
import { DocsSection } from "@/sections/docs/DocsSection";
import { ExtractedTextModal } from "@/sections/scope/ExtractedTextModal";
import { ReportAuditLog } from "@/shell/ReportAuditLog";
import { lifecycleColor, lifecycleOf } from "@/core/ui/reportStatus";
import { openPdfAtPage } from "@/core/api/pdf";

interface Props {
  reportId: string;
  focusActionItemId?: string;
  onBack: () => void;
  /** #C6: open another report (a carve-out's linked/placeholder SOC 1). */
  onOpenReport?: (reportId: string) => void;
}

type SectionKey = "scope" | "bridge" | "controls" | "cuecs" | "docs";

export interface Focus {
  section: SectionKey;
  targetId: string | null;
}

export function ReportDetail({ reportId, focusActionItemId, onBack, onOpenReport }: Props) {
  const [report, setReport] = useState<Report | null>(null);
  const [scopeChecks, setScopeChecks] = useState<ScopeCheck[]>([]);
  const [openItems, setOpenItems] = useState<ActionItem[]>([]);
  const [focus, setFocus] = useState<Focus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [extractedOpen, setExtractedOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, items] = await Promise.all([
        getReport(reportId),
        listActionItems({ status: "open", reportId }),
      ]);
      setReport(data.report);
      setScopeChecks(data.scope_checks);
      setOpenItems(items);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Resolve the focus target (#45). target_type maps to a section key —
  // scope_check → scope, exception → controls, cuec → cuecs, bridge_evidence
  // → bridge. Carve-out sub-rows have target_type=scope_check + the carve-out
  // name in the title, so the scope section handles them too.
  useEffect(() => {
    if (!focusActionItemId) {
      setFocus(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const a = await getActionItem(focusActionItemId);
        if (cancelled) return;
        const section: SectionKey =
          a.section === "scope" ||
          a.section === "bridge" ||
          a.section === "controls" ||
          a.section === "cuecs" ||
          a.section === "docs"
            ? a.section
            : "scope";
        setFocus({ section, targetId: a.target_id });
      } catch {
        // If we can't resolve, drop the focus silently.
        setFocus(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [focusActionItemId]);

  // Auto-poll while ingest is still running.
  useEffect(() => {
    if (report?.status !== "uploading" && report?.status !== "processing") return;
    const id = setInterval(() => void refresh(), 5_000);
    return () => clearInterval(id);
  }, [report?.status, refresh]);

  const sectionCounts = useMemo(() => {
    const counts: Record<SectionKey, number> = {
      scope: 0,
      bridge: 0,
      controls: 0,
      cuecs: 0,
      docs: 0,
    };
    for (const it of openItems) {
      if (it.section in counts) counts[it.section as SectionKey] += 1;
    }
    return counts;
  }, [openItems]);

  // #14 — derive section-start PDF page from C1 / C2 ai_data so each header
  // can render "View in PDF" alongside the status badge. Controls/CUECs use
  // the lowest page among their rows (computed inside each section).
  const scopePage = useMemo<number | null>(() => {
    const c1 = scopeChecks.find((c) => c.check_num === 1);
    const p = (c1?.ai_data as Record<string, unknown> | null)?.system_description_start_page;
    return typeof p === "number" ? p : null;
  }, [scopeChecks]);

  if (loading && !report) {
    return (
      <Box padding="l" textAlign="center">
        <Spinner /> Loading report…
      </Box>
    );
  }

  if (error) {
    return (
      <Box padding="l">
        <Alert type="error" header="Failed to load report" action={<Button onClick={refresh}>Retry</Button>}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!report) return null;

  // #C6: a carve-out placeholder (no PDF yet) opens to an upload-only view —
  // no scope/controls/CUEC cards until a SOC 1 is uploaded.
  if (report.status === "awaiting_upload") {
    return <PlaceholderUpload report={report} onBack={onBack} onUploaded={refresh} />;
  }

  const c2 = scopeChecks.find((c) => c.check_num === 2);
  // Bridge Letter is gated on TWO things (#19): C2 verdict is Partial AND
  // the user has explicitly accepted that verdict. The user can also
  // override C2's status via the Edit-fields modal (#22) — that override
  // wins over AI's computed status.
  const c2OverrideStatus = (c2?.user_overrides as Record<string, unknown> | null)?.c2_override_status as
    | string
    | undefined;
  const c2EffectiveStatus = (c2OverrideStatus ?? c2?.status ?? "").toLowerCase();
  const c2Partial = c2EffectiveStatus === "partial";
  const c2Decision = (c2?.user_decision ?? "").toLowerCase();
  // #119: the Bridge section appears once the user decides C2 = Partial (an
  // explicit "accept with a bridge letter" answer — partial is never silently
  // auto-accepted). Legacy "accepted" on a partial C2 still counts.
  const showBridge =
    c2Decision === "partial" || (c2Partial && c2Decision === "accepted");

  const sectionStatus = report.status;
  const focusFor = (key: SectionKey) => (focus?.section === key ? focus : null);

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={onBack} iconName="arrow-left">
              Back to reports
            </Button>
            <Button
              iconName="external"
              onClick={() => void openPdfAtPage(reportId, 1)}
              disabled={!report.pdf_s3_key}
            >
              View PDF
            </Button>
            <Button iconName="file" onClick={() => setExtractedOpen(true)} disabled={report.status !== "ready"}>
              View extracted text
            </Button>
            <Button onClick={refresh} iconName="refresh" disabled={loading} />
          </SpaceBetween>
        }
      >
        {/* #107: the lifecycle badge sits next to the title (left), vertically
            centered — not isolated on the far right. Reset to body size so it
            doesn't inherit the h1 scale. */}
        <span style={{ display: "inline-flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          {report.service_org_name || "(extracting…)"}
          <span style={{ fontSize: "14px", fontWeight: 400 }}>
            <StatusIndicator type={lifecycleColor(lifecycleOf(report))}>
              {lifecycleOf(report)}
            </StatusIndicator>
          </span>
        </span>
      </Header>

      <ExtractedTextModal
        reportId={reportId}
        visible={extractedOpen}
        onDismiss={() => setExtractedOpen(false)}
      />

      {report.status === "processing" && (
        <Alert type="info" header="Ingest running">
          AI is extracting scope, controls, and CUECs from the PDF. This page polls every 5 seconds and
          refreshes when ready.
        </Alert>
      )}

      {report.status === "error" && (
        <Alert type="error" header="Ingest failed">
          Check CloudWatch logs for the ingest Lambda. Re-uploading should retry.
        </Alert>
      )}

      <ScopeSection
        reportId={reportId}
        scopeChecks={scopeChecks}
        onUpdated={refresh}
        // #C7: the bridge now lives inside Scope, so its open count rolls into
        // the Scope badge.
        openCount={sectionCounts.scope + (showBridge ? sectionCounts.bridge : 0)}
        openItems={openItems}
        pdfPage={scopePage}
        focus={focusFor("scope")}
        bridgeFocused={focus?.section === "bridge"}
        onOpenReport={onOpenReport}
        bridgeCard={
          showBridge ? (
            <BridgeSection
              reportId={reportId}
              openCount={sectionCounts.bridge}
              onUpdated={refresh}
              focus={focusFor("bridge")}
            />
          ) : undefined
        }
      />
      <ControlsSection
        reportId={reportId}
        reportStatus={sectionStatus}
        openCount={sectionCounts.controls}
        focus={focusFor("controls")}
        sectionPage={report.section_iv_start_page ?? null}
        onItemsChanged={refresh}
      />
      <CuecsSection
        reportId={reportId}
        reportStatus={sectionStatus}
        openCount={sectionCounts.cuecs}
        focus={focusFor("cuecs")}
        sectionPage={report.cuecs_section_start_page ?? null}
        onItemsChanged={refresh}
      />
      {/* #C7: the Bridge Letter is rendered inside ScopeSection (as C7), not as a
          standalone section — see the bridgeCard prop above. */}
      <DocsSection report={report} onUpdated={refresh} />
      <ReportAuditLog reportId={reportId} />
    </SpaceBetween>
  );
}
